/**
 * `/reports/sos` — the morning S.O.S report. Port of the demo's `buildSOS()`
 * (line 2635) and `renderSOS()` (line 2705).
 *
 * Service Operations Summary: the report the original aftermarket programme ran
 * by hand every morning, compiled at 06:00 from the condition register, the
 * alerts, the technician app, the approvals queue, the parts pipeline and the
 * customer-satisfaction feed. Four sections, in the order the original had
 * them — hotspots, activity, work in progress, evaluation and prompt — under a
 * row of headline measures for the reporting week and a card per site.
 *
 * Every figure opens the rows behind it in the source drawer. That is the
 * point of the page: a morning report nobody can check is a morning report
 * nobody believes.
 *
 * ## Data
 *
 * Five domains: `work-orders`, `telemetry` (shift records), `parts`
 * (inventory and orders), `service` (technician-app jobs) and `people`
 * (technician presence and the customer-satisfaction table). Alerts and
 * incidents come from the narrative seeds.
 *
 * ## Declared omissions
 *
 * - **The DOCX export.** The demo's report pane offers a Word file. Document
 *   generation is a Tier 3 roadmap row, so this page says so in one line
 *   rather than shipping a download button that does nothing.
 *
 * @see src/versions/v1/modules/reports/SosReportScene.transform.ts
 * @see docs/logic/reports/sos.md
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  InlineError,
  SkeletonRows,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { DocFoot } from '../../shared/docs/DocFoot';
import { Letterhead } from '../../shared/docs/Letterhead';
import { FLEET, MINES, MINE_ORDER } from '../../data/core';
import type { MineCode } from '../../schema/machine';
import { CONDITION_BAND_LABEL } from '../../schema/machine';
import { unpackTable } from '../../shared/columnar';
import { askOem } from '../../shared/assistant/ask';
import {
  fmt$,
  groupNum,
  mineWeek,
  WK,
  type MineWeek,
} from '../../shared/oem-engine';
import { BandPill, MeasureTile, SceneSection, SourceLink } from '../../shared/oem-tiles';
import {
  useOemData,
  type CustomerSatisfactionResponse,
  type ShiftDailyRow,
  type WorkOrder,
} from '../../shared/useOemData';
import { useRowsDrawer, type RowsDrawer } from '../service/source-slice';
import {
  activity,
  evaluation,
  headline,
  hotspots,
  isScope,
  scopeLine,
  scopeSites,
  SOS_DEFINITIONS,
  SOS_DISTRIBUTION,
  sosDocId,
  workInProgress,
  type EvaluationRow,
  type SosCtx,
} from './SosReportScene.transform';

/** Demo line 2706. The page's standfirst, verbatim. */
const STANDFIRST =
  'Service Operations Summary — the morning report the original programme ran by hand, now ' +
  'compiled at 06:00 from the condition register, alerts, the technician app, approvals, the ' +
  'parts pipeline and the CSS feed. Every figure opens its rows.';

export function SosReportScene() {
  const [params, setParams] = useSearchParams();
  const scopeParam = params.get('site');
  const scope: MineCode | null = isScope(scopeParam) ? scopeParam : null;
  const sites = useMemo(() => scopeSites(scope), [scope]);

  const workOrders = useOemData('work-orders');
  const telemetry = useOemData('telemetry');
  const parts = useOemData('parts');
  const service = useOemData('service');
  const people = useOemData('people');

  const ctx = useMemo<SosCtx | null>(() => {
    if (!workOrders.data || !telemetry.data || !parts.data || !service.data || !people.data) {
      return null;
    }
    return {
      workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
      shiftDaily: unpackTable<ShiftDailyRow>(telemetry.data.shift_daily),
      inventory: parts.data.inventory,
      orders: parts.data.orders,
      techJobs: service.data.tapp_jobs,
      techs: people.data.tapp_techs,
      css: unpackTable<CustomerSatisfactionResponse>(people.data.css),
    };
  }, [workOrders.data, telemetry.data, parts.data, service.data, people.data]);

  const weeks = useMemo<MineWeek[]>(
    () => (ctx ? sites.map((m) => mineWeek(m, ctx)) : []),
    [ctx, sites],
  );
  const measures = useMemo(() => headline(weeks), [weeks]);
  const spots = useMemo(() => (ctx ? hotspots(sites, ctx) : []), [ctx, sites]);
  const activityRows = useMemo(() => (ctx ? activity(sites, ctx) : []), [ctx, sites]);
  const wip = useMemo(
    () => (ctx ? workInProgress(sites, ctx) : null),
    [ctx, sites],
  );
  const evaluationRows = useMemo(() => (ctx ? evaluation(sites, ctx) : []), [ctx, sites]);

  const openRows = useRowsDrawer();
  const error =
    workOrders.error ?? telemetry.error ?? parts.error ?? service.error ?? people.error;

  const scopeLabel = scope ? MINES[scope].name : 'all sites';

  return (
    <SurfacePageLayout width="full">
      <Commentable name="SosReportScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-8" as="div">
            <SurfacePageHeader
              kicker="Reports"
              title="S.O.S daily report"
              description={STANDFIRST}
              actions={
                <div className="flex flex-wrap items-center gap-2">
                  <label className="sr-only" htmlFor="sos-site">
                    Report scope
                  </label>
                  <select
                    id="sos-site"
                    value={scope ?? 'all'}
                    onChange={(e) =>
                      setParams(e.target.value === 'all' ? {} : { site: e.target.value }, {
                        replace: true,
                      })
                    }
                    className="rounded-lg border px-2.5 py-1.5 text-[12.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                    style={{
                      borderColor: 'var(--hairline)',
                      background: 'var(--surface-2)',
                      color: 'var(--ink-primary)',
                    }}
                  >
                    <option value="all">All sites</option>
                    {MINE_ORDER.map((m) => (
                      <option key={m} value={m}>
                        {MINES[m].name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() =>
                      askOem(
                        `Generate today's S.O.S report${scope ? ` for ${MINES[scope].name}` : ''}`,
                      )
                    }
                    className="rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                    style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}
                  >
                    Ask the assistant
                  </button>
                  <HelpAnchor doc="reports/sos" />
                </div>
              }
            />

            <AskStrip view="sos" />

            {error ? (
              <InlineError>{`The report did not compile: ${error.message}`}</InlineError>
            ) : !ctx || !wip ? (
              <SkeletonRows count={12} />
            ) : (
              <>
                <Commentable name="SosMasthead">
                  <div
                    className="rounded-2xl border px-4 py-3"
                    style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                  >
                    <Letterhead
                      product
                      docType="S.O.S daily report"
                      docId={sosDocId(scope)}
                      date="09 Sep 2026 06:00"
                      classification="Internal — service operations"
                    />
                    <h2
                      className="text-[17px] font-semibold tracking-tight"
                      style={{ color: 'var(--ink-primary)' }}
                    >
                      S.O.S · Service Operations Summary — {scopeLabel}
                    </h2>
                    <p className="mt-1 text-[12px]" style={{ color: 'var(--ink-secondary)' }}>
                      {scopeLine(sites)}
                    </p>
                  </div>
                </Commentable>

                <Commentable name="SosHeadline">
                  <div className="min-w-0">
                    <SceneSection title="The week" subtitle={WK.label}>
                      <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-5">
                        <MeasureTile
                          eyebrow="Fleet availability"
                          value={measures.availability}
                          unit="%"
                          tone={measures.availability >= 80 ? 'good' : 'warn'}
                          detail={`${measures.machines} machines across ${sites.length} site${sites.length > 1 ? 's' : ''}`}
                          onSelect={() =>
                            openRows('shift', weekShiftRows(ctx, sites), {
                              title: 'Shift records behind the availability figure',
                              filter: `${WK.start} → ${WK.end}`,
                              total: ctx.shiftDaily.length,
                            })
                          }
                          selectHint="Opens the shift records the figure is derived from"
                        />
                        <MeasureTile
                          eyebrow="Breakdowns"
                          value={measures.breakdowns}
                          unit={`of ${measures.workOrders} orders raised`}
                          tone={measures.breakdowns ? 'bad' : 'good'}
                          detail={`${measures.downHours} h of unplanned downtime`}
                          onSelect={() =>
                            openRows('wos', breakdownRows(ctx, sites), {
                              title: 'Breakdowns raised this week',
                              filter: `wo_type=Breakdown, ${WK.start} → ${WK.end}`,
                            })
                          }
                          selectHint="Opens the breakdown work orders"
                        />
                        <MeasureTile
                          eyebrow="Reactive share"
                          value={measures.reactivePct}
                          unit="%"
                          tone={measures.reactivePct > 50 ? 'bad' : 'warn'}
                          detail={`${measures.reactive} of ${measures.workOrders} orders were not preventive`}
                        />
                        <MeasureTile
                          eyebrow="Cost this week"
                          value={fmt$(measures.cost)}
                          unit="direct + lost production"
                          detail={`${groupNum(measures.diesel)} L diesel · ${groupNum(measures.kwh)} kWh`}
                        />
                        <MeasureTile
                          eyebrow="Emissions"
                          value={measures.co2Tonnes}
                          unit="t CO₂e"
                          detail="diesel × 2.68 kg/L plus grid kWh at the site factor"
                        />
                      </div>
                    </SceneSection>
                  </div>
                </Commentable>

                <Commentable name="SosSiteCards">
                  <div className="min-w-0">
                    <SceneSection
                      title="Site by site"
                      subtitle={`the reporting week, ${WK.short}`}
                    >
                      <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-3">
                        {weeks.map((week) => (
                          <SiteWeekCard key={week.mine} week={week} openRows={openRows} />
                        ))}
                      </div>
                    </SceneSection>
                  </div>
                </Commentable>

                <Commentable name="SosHotspots">
                  <div className="min-w-0">
                    <SceneSection
                      title="1. Daily hotspots"
                      subtitle={`${spots.length} machines in the very-poor or major-issue bands`}
                      actions={
                        <SourceLink
                          onClick={() =>
                            openRows('health', spots.map((s) => s.worst), {
                              title: 'Condition register · hotspots',
                              filter: `machine_id in ${spots.length} hotspots`,
                            })
                          }
                        >
                          condition register
                        </SourceLink>
                      }
                    >
                      <Table className="min-w-[900px]">
                        <THead>
                          <TR>
                            <TH>Machine</TH>
                            <TH>Site</TH>
                            <TH>Condition</TH>
                            <TH>Component</TH>
                            <TH>What is happening</TH>
                            <TH>Next action · owner</TH>
                          </TR>
                        </THead>
                        <TBody>
                          {spots.map((spot) => (
                            <TR key={spot.machine.machine_id}>
                              <TD className="whitespace-nowrap">
                                <Link
                                  to={`/machines/${spot.machine.machine_id}`}
                                  className="mono underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
                                  style={{ color: 'var(--accent)' }}
                                >
                                  {spot.machine.machine_id}
                                </Link>{' '}
                                <span
                                  className="mono text-[10.5px]"
                                  style={{ color: 'var(--ink-tertiary)' }}
                                >
                                  {spot.machine.model}
                                </span>
                              </TD>
                              <TD className="whitespace-nowrap">{spot.siteShort}</TD>
                              <TD>
                                <BandPill band={spot.band}>{spot.worst.health_score}/100</BandPill>
                              </TD>
                              <TD className="text-[11.5px]">{spot.worst.component}</TD>
                              <TD className="text-[11.5px]">{spot.note}</TD>
                              <TD className="text-[11.5px]">{spot.next}</TD>
                            </TR>
                          ))}
                        </TBody>
                      </Table>
                    </SceneSection>
                  </div>
                </Commentable>

                <Commentable name="SosActivity">
                  <div className="min-w-0">
                    <SceneSection title="2. Activity" subtitle="last 24 hours">
                      <Table className="min-w-[760px]">
                        <THead>
                          <TR>
                            <TH>What</TH>
                            <TH className="text-right">Count</TH>
                            <TH>Detail</TH>
                          </TR>
                        </THead>
                        <TBody>
                          {activityRows.map((row) => (
                            <TR key={row.key}>
                              <TD>{row.what}</TD>
                              <TD className="mono text-right tabular-nums">{row.count}</TD>
                              <TD className="text-[11.5px]">{row.detail}</TD>
                            </TR>
                          ))}
                        </TBody>
                      </Table>
                    </SceneSection>
                  </div>
                </Commentable>

                <Commentable name="SosWorkInProgress">
                  <div className="min-w-0">
                    <SceneSection title="3. Work in progress" subtitle="what is waiting on somebody">
                      <Table className="min-w-[760px]">
                        <THead>
                          <TR>
                            <TH>Queue</TH>
                            <TH className="text-right">Items</TH>
                            <TH>Oldest / most urgent</TH>
                            <TH>Rows</TH>
                          </TR>
                        </THead>
                        <TBody>
                          {wip.rows.map((row) => (
                            <TR key={row.key}>
                              <TD>{row.queue}</TD>
                              <TD className="mono text-right tabular-nums">{row.count}</TD>
                              <TD className="text-[11.5px]">{row.detail}</TD>
                              <TD>
                                <WipSource row={row.key} wip={wip.slices} openRows={openRows} />
                              </TD>
                            </TR>
                          ))}
                        </TBody>
                      </Table>
                    </SceneSection>
                  </div>
                </Commentable>

                <Commentable name="SosEvaluation">
                  <div className="min-w-0">
                    <SceneSection title="4. Evaluation and prompt" subtitle="one question per site lead">
                      <Table className="min-w-[960px]">
                        <THead>
                          <TR>
                            <TH>Site</TH>
                            <TH className="text-right">Availability</TH>
                            <TH className="text-right">Breakdowns</TH>
                            <TH className="text-right">Cost this week</TH>
                            <TH className="text-right">CSS</TH>
                            <TH>Prompt for the site lead this morning</TH>
                          </TR>
                        </THead>
                        <TBody>
                          {evaluationRows.map((row) => (
                            <EvaluationTableRow key={row.mine} row={row} />
                          ))}
                        </TBody>
                      </Table>
                      <p className="mt-2 text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                        {SOS_DEFINITIONS}
                      </p>
                      <DocFoot docId={sosDocId(null)} controlled={false} />
                    </SceneSection>
                  </div>
                </Commentable>

                <Commentable name="SosDistribution">
                  <section
                    className="grid grid-cols-1 gap-4 rounded-2xl border p-4 @3xl:grid-cols-2"
                    style={{ borderColor: 'var(--hairline)', background: 'var(--surface-1)' }}
                  >
                    <div>
                      <div
                        className="text-[10px] font-semibold uppercase tracking-[0.08em]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        Distribution
                      </div>
                      <p className="mt-1 text-[12px]" style={{ color: 'var(--ink-secondary)' }}>
                        {SOS_DISTRIBUTION}
                      </p>
                    </div>
                    <div>
                      <div
                        className="text-[10px] font-semibold uppercase tracking-[0.08em]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        As a document
                      </div>
                      <p className="mt-1 text-[12px]" style={{ color: 'var(--ink-secondary)' }}>
                        This proof of concept renders the report in the page and opens the rows
                        behind every figure. Producing it as a Word or PowerPoint file — the form
                        it is circulated in today — is on the roadmap and is not wired up here.
                      </p>
                    </div>
                  </section>
                </Commentable>
              </>
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** The shift records in the reporting week, for the availability tile. */
function weekShiftRows(ctx: SosCtx, sites: readonly MineCode[]) {
  const ids = new Set(FLEET.filter((f) => sites.includes(f.mine)).map((f) => f.machine_id));
  return ctx.shiftDaily.filter(
    (s) => ids.has(s.machine_id) && s.date >= WK.start && s.date <= WK.end,
  );
}

/** The breakdown work orders behind the headline tile. */
function breakdownRows(ctx: SosCtx, sites: readonly MineCode[]): WorkOrder[] {
  return ctx.workOrders.filter(
    (w) =>
      sites.includes(w.mine) &&
      w.wo_type === 'Breakdown' &&
      w.created.slice(0, 10) >= WK.start &&
      w.created.slice(0, 10) <= WK.end,
  );
}

/** Demo line 2564. One site's reporting week, as a card. */
function SiteWeekCard({ week, openRows }: { week: MineWeek; openRows: RowsDrawer }) {
  const machines = FLEET.filter((f) => f.mine === week.mine);
  return (
    <article
      className="flex min-w-0 flex-col gap-2 rounded-2xl border p-4"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-[14px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
          <Link to={`/sites/${week.mine}`} className="hover:underline">
            {week.name}
          </Link>
        </h3>
        <span className="mono text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
          {week.n} machines · {week.days} machine-days
        </span>
      </header>

      <div className="grid grid-cols-3 gap-x-3 gap-y-1.5 text-[11.5px]">
        <Figure label="Availability" value={`${week.avail}%`} />
        <Figure label="Breakdowns" value={String(week.breakdowns)} />
        <Figure label="Reactive" value={`${week.reactive} of ${week.wos}`} />
        <Figure label="Downtime" value={`${week.down_h} h`} />
        <Figure label="Cost" value={fmt$(week.cost)} />
        <Figure label="CO₂e" value={`${week.co2_t} t`} />
      </div>

      <div className="flex flex-wrap gap-1">
        {(['major', 'vpoor', 'poor', 'fair', 'good'] as const)
          .filter((band) => week.bands[band] > 0)
          .map((band) => (
            <BandPill key={band} band={band}>
              {CONDITION_BAND_LABEL[band]} {week.bands[band]}
            </BandPill>
          ))}
      </div>

      <p className="text-[11.5px]" style={{ color: 'var(--ink-secondary)' }}>
        <b>{week.flagged.length}</b> flagged · <b>{week.drafts.length}</b> drafts ·{' '}
        <b>{week.late}</b> late PMs · <b>{week.low.length}</b> store lines low ·{' '}
        <b>{week.alerts.length}</b> alerts · <b>{week.pm_due.length}</b> PMs due
      </p>

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
        <SourceLink
          onClick={() =>
            openRows('wos', week.drafts, {
              title: `Drafted work orders · ${week.name}`,
              filter: `mine=${week.mine}, status=Draft…`,
            })
          }
        >
          drafts
        </SourceLink>
        <SourceLink
          onClick={() =>
            openRows('inv', week.low, {
              title: `Below minimum · ${week.mine} site store`,
              filter: `location=${week.mine} site store`,
            })
          }
        >
          low stock
        </SourceLink>
        <SourceLink onClick={() => openRows('fleet', machines, { filter: `mine=${week.mine}` })}>
          fleet
        </SourceLink>
      </div>
    </article>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div
        className="text-[10px] font-semibold uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-tertiary)' }}
      >
        {label}
      </div>
      <div className="mono text-[13.5px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
        {value}
      </div>
    </div>
  );
}

function EvaluationTableRow({ row }: { row: EvaluationRow }) {
  return (
    <TR>
      <TD className="whitespace-nowrap">
        <Link
          to={`/sites/${row.mine}`}
          className="underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
          style={{ color: 'var(--accent)' }}
        >
          {row.name}
        </Link>
      </TD>
      <TD className="mono text-right tabular-nums">{row.week.avail}%</TD>
      <TD className="mono text-right tabular-nums">{row.week.breakdowns}</TD>
      <TD className="mono text-right tabular-nums">{fmt$(row.week.cost)}</TD>
      <TD className="mono text-right tabular-nums">
        {row.css == null ? '—' : row.css}
        {row.cssResponses ? (
          <span style={{ color: 'var(--ink-tertiary)' }}> ({row.cssResponses})</span>
        ) : null}
      </TD>
      <TD className="text-[11.5px]">{row.prompt}</TD>
    </TR>
  );
}

/** The source link for one work-in-progress queue. */
function WipSource({
  row,
  wip,
  openRows,
}: {
  row: string;
  wip: ReturnType<typeof workInProgress>['slices'];
  openRows: RowsDrawer;
}) {
  if (row === 'drafts') {
    return (
      <SourceLink onClick={() => openRows('wos', wip.drafts, { filter: 'status=Draft…' })}>
        open
      </SourceLink>
    );
  }
  if (row === 'requisitions') {
    return (
      <SourceLink
        onClick={() => openRows('orders', wip.requisitions, { filter: 'status=Draft…' })}
      >
        open
      </SourceLink>
    );
  }
  if (row === 'transit') {
    return (
      <SourceLink onClick={() => openRows('orders', wip.inTransit, { filter: 'in transit' })}>
        open
      </SourceLink>
    );
  }
  if (row === 'stock') {
    return (
      <SourceLink
        onClick={() => openRows('inv', wip.belowMin, { filter: 'qty_on_hand < min_level' })}
      >
        open
      </SourceLink>
    );
  }
  return (
    <SourceLink
      onClick={() =>
        openRows(
          'xreach',
          wip.openJobs.map((j) => ({
            ...j,
            machine_id: j.machine,
            labour_h: j.labour_lines.reduce((a, l) => a + l.hours, 0),
            parts: j.parts_used.map((p) => p.part_no).join(' '),
          })),
          { filter: 'status≠Completed' },
        )
      }
    >
      open
    </SourceLink>
  );
}
